import { useState } from 'react';
import { useLocation } from 'wouter';
import { motion } from 'framer-motion';
import { SoraFloMark } from '@/components/soraflo-logo';
import { useLogin, useRegister } from '@workspace/api-client-react';
import { useAuthStore } from '@/lib/auth-store';
import { Input, PasswordInput, Button, Label } from '@/components/ui-elements';
import { useToast } from '@/hooks/use-toast';

export function AuthPage({ isRegister = false }: { isRegister?: boolean }) {
  const [_, setLocation] = useLocation();
  const setToken = useAuthStore(s => s.setToken);
  const { toast } = useToast();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');

  const loginMutation = useLogin();
  const registerMutation = useRegister();

  const isPending = loginMutation.isPending || registerMutation.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isRegister) {
        const res = await registerMutation.mutateAsync({ data: { email, password, name } });
        setToken(res.token);
        toast({ title: "Account created", description: "Welcome to SoraFlo." });
      } else {
        const res = await loginMutation.mutateAsync({ data: { email, password } });
        setToken(res.token);
        toast({ title: "Welcome back", description: "Successfully logged in." });
      }
      setLocation('/');
    } catch (err: any) {
      toast({ 
        title: "Authentication Failed", 
        description: err?.message || "Please check your credentials and try again.",
        variant: "destructive" 
      });
    }
  };

  return (
    <div className="min-h-screen w-full flex bg-background relative overflow-hidden">
      <div className="w-full max-w-[460px] m-auto z-10 p-10 bg-card rounded-lg border border-border shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col items-center mb-8"
        >
          <SoraFloMark size={40} className="text-primary mb-5" />
          <h1 className="text-[20px] text-foreground font-semibold mb-1">
            {isRegister ? 'Create an account' : 'Welcome back'}
          </h1>
          <p className="text-muted-foreground text-[14px] text-center">
            {isRegister ? 'Enter your details to get started with SoraFlo.' : 'Enter your credentials to access your dashboard.'}
          </p>
        </motion.div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {isRegister && (
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-foreground font-medium text-sm">Full Name</Label>
              <Input 
                id="name" 
                placeholder="John Doe" 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
                required 
                className="bg-background border-input focus-visible:ring-ring text-sm h-9 rounded-md"
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-foreground font-medium text-sm">Email Address</Label>
            <Input 
              id="email" 
              type="email" 
              placeholder="you@example.com" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              required 
              className="bg-background border-input focus-visible:ring-ring text-sm h-9 rounded-md"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-foreground font-medium text-sm">Password</Label>
            <PasswordInput
              id="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="bg-background border-input focus-visible:ring-ring text-sm h-9 rounded-md"
            />
          </div>

          <Button 
            type="submit" 
            className="w-full mt-2 bg-primary hover:bg-primary/90 text-primary-foreground h-9 rounded-md text-sm font-medium border-0" 
            isLoading={isPending}
          >
            {isRegister ? 'Create Account' : 'Sign In'}
          </Button>
        </form>

        <div className="mt-6 text-center text-[14px] text-muted-foreground">
          {isRegister ? (
            <p>Already have an account?{' '}
              <span onClick={() => setLocation('/auth/login')} className="text-primary hover:underline cursor-pointer font-medium">Sign in</span>
            </p>
          ) : (
            <p>Don't have an account?{' '}
              <span onClick={() => setLocation('/auth/register')} className="text-primary hover:underline cursor-pointer font-medium">Create one</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
